import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useMemo } from 'react';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import Tooltip from '@mui/material/Tooltip';
import CreateEditPackageModal from './CreateEditPackageModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import BulkDeleteModal from './BulkDeleteModal';

const SmsCreditPackageListLayer = () => {
    const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
    const [packageToEdit, setPackageToEdit] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [packageToDelete, setPackageToDelete] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Selection state
    const [selectedIds, setSelectedIds] = useState([]);

    // Bulk action modal
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

    // Sample data - replace with actual API data
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const samplePackages = [
        {
            id: 1,
            packageName: 'Basic Pack',
            credits: 1000,
            price: 350,
            status: 'Active',
            createdDate: '04 Feb 2025, 06:41 PM',
        },
        {
            id: 2,
            packageName: 'Standard Pack',
            credits: 5000,
            price: 1500,
            status: 'Active',
            createdDate: '04 Feb 2025, 06:31 PM',
        },
        {
            id: 3,
            packageName: 'Premium Pack',
            credits: 12500,
            price: 3500,
            status: 'Active',
            createdDate: '04 Feb 2025, 04:59 PM',
        },
        {
            id: 4,
            packageName: 'Enterprise Pack',
            credits: 25000,
            price: 6500,
            status: 'Active',
            createdDate: '04 Feb 2025, 04:57 PM',
        },
        {
            id: 5,
            packageName: 'Starter Pack',
            credits: 500,
            price: 200,
            status: 'Inactive',
            createdDate: '04 Feb 2025, 04:50 PM',
        },
        {
            id: 6,
            packageName: 'Mega Pack',
            credits: 50000,
            price: 12000,
            status: 'Active',
            createdDate: '30 Jan 2025, 05:42 PM',
        },
        {
            id: 7,
            packageName: 'Trial Pack',
            credits: 100,
            price: 50,
            status: 'Inactive',
            createdDate: '27 Nov 2024, 08:01 PM',
        },
        {
            id: 8,
            packageName: 'Bulk Pack',
            credits: 100000,
            price: 22000,
            status: 'Active',
            createdDate: '03 May 2024, 06:10 PM',
        },
    ];

    // Get selected packages data
    const selectedPackages = useMemo(() => {
        return samplePackages.filter(pkg => selectedIds.includes(pkg.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds, samplePackages]);

    // Check if all items are selected
    const isAllSelected = samplePackages.length > 0 && selectedIds.length === samplePackages.length;
    const isIndeterminate = selectedIds.length > 0 && selectedIds.length < samplePackages.length;

    // Handle select all
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(samplePackages.map(pkg => pkg.id));
        } else {
            setSelectedIds([]);
        }
    };

    // Handle individual row selection
    const handleSelectRow = (id) => {
        setSelectedIds(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    // Clear selection
    const clearSelection = () => {
        setSelectedIds([]);
    };

    // Create handlers
    const handleCreateClick = () => {
        setPackageToEdit(null);
        setCreateEditModalOpen(true);
    };

    // Edit handlers
    const handleEditClick = (pkg) => {
        setPackageToEdit(pkg);
        setCreateEditModalOpen(true);
    };

    const handleCloseCreateEditModal = () => {
        setCreateEditModalOpen(false);
        setPackageToEdit(null);
    };

    const handleSavePackage = async (packageData) => {
        setIsLoading(true);
        try {
            if (packageToEdit) {
                // TODO: Call API to update package
                console.log('Updating package:', packageData);
            } else {
                // TODO: Call API to create package
                console.log('Creating package:', packageData);
            }
            await new Promise(resolve => setTimeout(resolve, 1000));
            handleCloseCreateEditModal();
            // TODO: Refresh the list
        } catch (error) {
            console.error('Error saving package:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Delete handlers
    const handleDeleteClick = (pkg) => {
        setPackageToDelete(pkg);
        setDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false);
        setPackageToDelete(null);
    };

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        try {
            // TODO: Call API to delete package
            console.log('Deleting package:', packageToDelete);
            await new Promise(resolve => setTimeout(resolve, 1000));
            handleCloseDeleteModal();
            // TODO: Refresh the list
        } catch (error) {
            console.error('Error deleting package:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Bulk Delete handlers
    const handleBulkDeleteClick = () => {
        setBulkDeleteModalOpen(true);
    };

    const handleCloseBulkDeleteModal = () => {
        setBulkDeleteModalOpen(false);
    };

    const handleConfirmBulkDelete = async () => {
        setIsLoading(true);
        try {
            // TODO: Call API to delete all selected packages
            console.log('Bulk deleting packages:', selectedPackages);
            await new Promise(resolve => setTimeout(resolve, 1000));
            handleCloseBulkDeleteModal();
            clearSelection();
            // TODO: Refresh the list
        } catch (error) {
            console.error('Error bulk deleting packages:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Status badge renderer
    const renderStatusBadge = (status) => {
        const statusClasses = {
            'Active': 'bg-success-focus text-success-main',
            'Inactive': 'bg-danger-focus text-danger-main'
        };

        return (
            <span className={`px-16 py-4 rounded-pill fw-medium text-sm ${statusClasses[status] || ''}`}>
                {status}
            </span>
        );
    };

    return (
        <div className="card">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value="All"
                            onChange={() => {}}
                            options={[
                                { value: 'All', label: 'All Status' },
                                { value: 'Active', label: 'Active' },
                                { value: 'Inactive', label: 'Inactive' },
                            ]}
                            placeholder="Status"
                            clearable={false}
                            small
                        />
                    </div>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <Tooltip title="Add New Package" placement="top" arrow>
                        <button
                            onClick={handleCreateClick}
                            className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                        >
                            <Icon icon="mdi:plus" width="18" />
                            <span>Add Package</span>
                        </button>
                    </Tooltip>
                    <Tooltip title="Refresh List" placement="top" arrow>
                        <button className="btn btn-sm btn-outline-secondary">
                            <Icon icon="mdi:refresh" />
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Bulk Actions Bar - Shows when items are selected */}
            {selectedIds.length > 0 && (
                <div className="card-header bg-light border-top d-flex flex-wrap align-items-center justify-content-between gap-3 py-2">
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-medium text-primary-600">
                            {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
                        </span>
                        <button
                            onClick={clearSelection}
                            className="btn btn-sm btn-link text-secondary p-0 ms-2"
                        >
                            Clear selection
                        </button>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {/* Delete All */}
                        <Tooltip title={`Delete ${selectedIds.length} package(s)`} placement="top" arrow>
                            <button
                                onClick={handleBulkDeleteClick}
                                className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                            >
                                <Icon icon="mingcute:delete-2-line" width="18" />
                                <span>Delete All ({selectedIds.length})</span>
                            </button>
                        </Tooltip>
                    </div>
                </div>
            )}

            <div className="card-body">
                <table className="table bordered-table mb-0">
                    <thead>
                        <tr>
                            <th scope="col">
                                <div className="form-check style-check d-flex align-items-center">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="checkAll"
                                        checked={isAllSelected}
                                        ref={el => {
                                            if (el) el.indeterminate = isIndeterminate;
                                        }}
                                        onChange={handleSelectAll}
                                    />
                                    <label className="form-check-label" htmlFor="checkAll">
                                        Sl No.
                                    </label>
                                </div>
                            </th>
                            <th scope="col">Package Name</th>
                            <th scope="col">Credits</th>
                            <th scope="col">Price</th>
                            <th scope="col">Status</th>
                            <th scope="col">Created Date</th>
                            <th scope="col">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {samplePackages.map((pkg, index) => (
                            <tr key={pkg.id} className={selectedIds.includes(pkg.id) ? 'table-active' : ''}>
                                <td>
                                    <div className="form-check style-check d-flex align-items-center">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`check${pkg.id}`}
                                            checked={selectedIds.includes(pkg.id)}
                                            onChange={() => handleSelectRow(pkg.id)}
                                        />
                                        <label className="form-check-label" htmlFor={`check${pkg.id}`}>
                                            {String(index + 1).padStart(2, '0')}
                                        </label>
                                    </div>
                                </td>
                                <td>
                                    <span className="fw-medium">{pkg.packageName}</span>
                                </td>
                                <td>
                                    <span className="px-12 py-4 rounded bg-primary-light text-primary-600 fw-semibold text-sm">
                                        {pkg.credits.toLocaleString()}
                                    </span>
                                </td>
                                <td>₹{pkg.price.toLocaleString()}</td>
                                <td>{renderStatusBadge(pkg.status)}</td>
                                <td>{pkg.createdDate}</td>
                                <td>
                                    <div className="d-flex align-items-center gap-1">
                                        {/* Edit Button */}
                                        <Tooltip title="Edit" placement="top" arrow>
                                            <button
                                                onClick={() => handleEditClick(pkg)}
                                                className="w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="mdi:pencil-outline" />
                                            </button>
                                        </Tooltip>

                                        {/* Delete Button */}
                                        <Tooltip title="Delete" placement="top" arrow>
                                            <button
                                                onClick={() => handleDeleteClick(pkg)}
                                                className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="mingcute:delete-2-line" />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <PaginationLimit />
            </div>

            {/* Create/Edit Modal */}
            <CreateEditPackageModal
                open={createEditModalOpen}
                handleClose={handleCloseCreateEditModal}
                handleSave={handleSavePackage}
                packageData={packageToEdit}
                isLoading={isLoading}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                open={deleteModalOpen}
                handleClose={handleCloseDeleteModal}
                handleConfirm={handleConfirmDelete}
                title="Delete SMS Credit Package"
                itemName={packageToDelete?.packageName}
                itemId={packageToDelete?.id}
                isLoading={isLoading}
            />

            {/* Bulk Delete Modal */}
            <BulkDeleteModal
                open={bulkDeleteModalOpen}
                handleClose={handleCloseBulkDeleteModal}
                handleConfirm={handleConfirmBulkDelete}
                packages={selectedPackages}
                isLoading={isLoading}
            />
        </div>
    );
};

export default SmsCreditPackageListLayer;
